import { useState } from 'react'
import { Alert, Button, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { BUTTON_ROW } from './cardParts'
import {
  monthsBefore,
  saveStartMonth,
  toBankStartMonth,
  toMonthNamer,
  toStartCandidates,
} from './limitedHistory'
import { SLOT_KIND, type Slot } from './statementUpload'

const STEP = {
  usedBefore: 'used_before',
  sinceStart: 'since_start',
  pickMonth: 'pick_month',
  allNeeded: 'all_needed',
} as const

type Step = (typeof STEP)[keyof typeof STEP]

interface StartMonthQuestionProps {
  accountSlots: Slot[]
  disabled: boolean
  onSaved: () => void
}

const Question = ({ children }: { children: string }) => (
  <Text variant={TypographyStyles.LABEL_MD} color={TextColors.HEADER}>
    {children}
  </Text>
)

const Answered = ({ children }: { children: string }) => (
  <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
    {children}
  </Text>
)

export const StartMonthQuestion = ({
  accountSlots,
  disabled,
  onSaved,
}: StartMonthQuestionProps) => {
  const [step, setStep] = useState<Step>(STEP.usedBefore)
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState<readonly string[] | null>(null)
  const candidates = toStartCandidates(accountSlots)
  if (candidates.length === 0) return null

  const { financialAccountId, bankHistoryStartsOn } = accountSlots[0]
  const bankStartMonth = toBankStartMonth(candidates, bankHistoryStartsOn)
  const { spansYears, name } = toMonthNamer([...candidates, bankStartMonth])
  const bankStart = name(bankStartMonth)
  const first = name(candidates[0])
  const locked = disabled || saving
  const needed = accountSlots.filter(
    ({ state }) => state.kind === SLOT_KIND.open
  ).length

  const save = async (notOpenMonths: readonly string[]) => {
    setSaving(true)
    setFailed(null)
    try {
      await saveStartMonth(financialAccountId, notOpenMonths)
      onSaved()
    } catch {
      setFailed(notOpenMonths)
    } finally {
      setSaving(false)
    }
  }

  const answerUsedBefore = () =>
    setStep(candidates.length === 1 ? STEP.allNeeded : STEP.sinceStart)
  const answerStartedLater = () =>
    candidates.length === 2
      ? void save(candidates.slice(0, 1))
      : setStep(STEP.pickMonth)

  const usedBefore = `Did you use it for your practice before ${bankStart}?`
  const sinceStart =
    !spansYears && candidates[0].endsWith('-01')
      ? 'Since the start of the year?'
      : `Since ${first}?`

  return (
    <div className="flex flex-col gap-2 rounded-md border border-neutral-200 p-3">
      {step === STEP.usedBefore && (
        <>
          <Question>{usedBefore}</Question>
          <div className={BUTTON_ROW}>
            <Button
              variant="secondary"
              size="sm"
              disabled={locked}
              onClick={answerUsedBefore}
            >
              Yes
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={locked}
              onClick={() => void save(candidates)}
            >
              {`No, ${bankStart} was the start`}
            </Button>
          </div>
        </>
      )}
      {step === STEP.sinceStart && (
        <>
          <Answered>{`Used before ${bankStart}: yes`}</Answered>
          <Question>{sinceStart}</Question>
          <div className={BUTTON_ROW}>
            <Button
              variant="secondary"
              size="sm"
              disabled={locked}
              onClick={() => setStep(STEP.allNeeded)}
            >
              {`Yes, since ${first}`}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={locked}
              onClick={answerStartedLater}
            >
              No, it started later
            </Button>
          </div>
        </>
      )}
      {step === STEP.pickMonth && (
        <>
          <Answered>{`Since ${first}: no`}</Answered>
          <Question>Which month did it start?</Question>
          <div className={BUTTON_ROW}>
            {candidates.slice(1).map((month) => (
              <Button
                key={month}
                variant="secondary"
                size="sm"
                disabled={locked}
                onClick={() => void save(monthsBefore(candidates, month))}
              >
                {name(month)}
              </Button>
            ))}
          </div>
        </>
      )}
      {step === STEP.allNeeded && (
        <Answered>
          {`Heard needs ${needed === 1 ? 'this statement' : `these ${needed} statements`} to add those transactions to your books.`}
        </Answered>
      )}
      {failed && (
        <Alert
          variant="error"
          description="Heard couldn't save your answer."
          ctaText="Try again"
          ctaOnClick={() => void save(failed)}
        />
      )}
    </div>
  )
}
